import { Suspense, lazy } from "react";
import { Toaster } from "@/components/ui/toaster";
import { Toaster as Sonner } from "@/components/ui/sonner";
import { TooltipProvider } from "@/components/ui/tooltip";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { HelmetProvider } from "react-helmet-async";
import { AuthProvider } from "@/hooks/useAuth";
import { BrandingProvider } from "@/hooks/useBranding";
import Index from "./pages/Index";

const Privacy = lazy(() => import("./pages/Privacy"));
const Terms = lazy(() => import("./pages/Terms"));
const HIPAA = lazy(() => import("./pages/HIPAA"));
const Accessibility = lazy(() => import("./pages/Accessibility"));
const Auth = lazy(() => import("./pages/Auth"));
const Dashboard = lazy(() => import("./pages/Dashboard"));
const ResetPassword = lazy(() => import("./pages/ResetPassword"));
const CaseStatus = lazy(() => import("./pages/CaseStatus"));
const NotFound = lazy(() => import("./pages/NotFound"));

// Admin routes
const AdminLayout = lazy(() => import("./pages/admin/AdminLayout"));
const AdminDashboard = lazy(() => import("./pages/admin/AdminDashboard"));
const AdminMembers = lazy(() => import("./pages/admin/AdminMembers"));
const AdminCancellations = lazy(() => import("./pages/admin/AdminCancellations"));
const AdminSupportCases = lazy(() => import("./pages/admin/AdminSupportCases"));
const AdminUsers = lazy(() => import("./pages/admin/AdminUsers"));
const AdminSettings = lazy(() => import("./pages/admin/AdminSettings"));
const BrandingSettingsPage = lazy(() => import("./pages/admin/settings/BrandingSettingsPage"));
const NotificationRecipientsPage = lazy(() => import("./pages/admin/settings/NotificationRecipientsPage"));
const ScheduledJobsPage = lazy(() => import("./pages/admin/settings/ScheduledJobsPage"));
const FeatureFlagsPage = lazy(() => import("./pages/admin/settings/FeatureFlagsPage"));
const AffiliateDiscountPage = lazy(() => import("./pages/admin/settings/AffiliateDiscountPage"));
const DisplayPreferencesPage = lazy(() => import("./pages/admin/settings/DisplayPreferencesPage"));
const AdminSecurity = lazy(() => import("./pages/admin/AdminSecurity"));
const AdminLogs = lazy(() => import("./pages/admin/AdminLogs"));
const AdminEmployerGroups = lazy(() => import("./pages/admin/AdminEmployerGroups"));
const AdminRevive = lazy(() => import("./pages/admin/AdminRevive"));

const queryClient = new QueryClient();

const PageLoader = () => (
  <div className="min-h-screen flex items-center justify-center">
    <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
  </div>
);

const App = () => (
  <HelmetProvider>
    <QueryClientProvider client={queryClient}>
      <AuthProvider>
        <BrandingProvider>
        <TooltipProvider>
          <Toaster />
          <Sonner />
          <BrowserRouter>
            <Suspense fallback={<PageLoader />}>
              <Routes>
                <Route path="/" element={<Index />} />
                <Route path="/privacy" element={<Privacy />} />
                <Route path="/terms" element={<Terms />} />
                <Route path="/hipaa" element={<HIPAA />} />
                <Route path="/accessibility" element={<Accessibility />} />

                <Route path="/auth" element={<Auth />} />
                <Route path="/sign-in" element={<Auth />} />
                <Route path="/reset-password" element={<ResetPassword />} />
                <Route path="/dashboard" element={<Dashboard />} />
                <Route path="/case-status" element={<CaseStatus />} />

                <Route path="/admin" element={<AdminLayout />}>
                  <Route index element={<AdminDashboard />} />
                  <Route path="members" element={<AdminMembers />} />
                  <Route path="cancellations" element={<AdminCancellations />} />
                  <Route path="support-cases" element={<AdminSupportCases />} />
                  <Route path="users" element={<AdminUsers />} />
                  <Route path="settings" element={<AdminSettings />} />
                  <Route path="settings/branding" element={<BrandingSettingsPage />} />
                  <Route path="settings/notifications" element={<NotificationRecipientsPage />} />
                  <Route path="settings/scheduled-jobs" element={<ScheduledJobsPage />} />
                  <Route path="settings/feature-flags" element={<FeatureFlagsPage />} />
                  <Route path="settings/affiliate-discount" element={<AffiliateDiscountPage />} />
                  <Route path="settings/display-preferences" element={<DisplayPreferencesPage />} />
                  <Route path="security" element={<AdminSecurity />} />
                  <Route path="logs" element={<AdminLogs />} />
                  <Route path="activity" element={<Navigate to="/admin/logs?tab=activity" replace />} />
                  <Route path="employer-groups" element={<AdminEmployerGroups />} />
                  <Route path="revive-reports" element={<AdminRevive />} />
                </Route>

                <Route path="*" element={<NotFound />} />
              </Routes>
            </Suspense>
          </BrowserRouter>
        </TooltipProvider>
      </BrandingProvider>
      </AuthProvider>
    </QueryClientProvider>
  </HelmetProvider>
);

export default App;
